import { useEffect, useRef } from 'react';

const useWaitingRoomAudioInputSync = ({
  meetingManager,
  audioInputDevices,
  activeMicDeviceId,
  setActiveMicDeviceId,
  onAudioInputSelected,
  setMenuDevices,
}) => {
  const knownIdsRef = useRef(new Set());
  const activeMicDeviceIdRef = useRef(activeMicDeviceId);
  const audioInputDevicesRef = useRef(audioInputDevices || []);
  const applyingRef = useRef(false);
  const pendingDeviceIdRef = useRef('');

  useEffect(() => {
    activeMicDeviceIdRef.current = activeMicDeviceId;
  }, [activeMicDeviceId]);

  useEffect(() => {
    audioInputDevicesRef.current = audioInputDevices || [];
  }, [audioInputDevices]);

  const normalizeDevice = (device) => ({
    deviceId: device?.deviceId || '',
    label: device?.label || '',
  });

  const mergeDevices = (sessionDevices, systemDevices) => {
    const byId = new Map();

    (sessionDevices || []).forEach((device) => {
      const normalized = normalizeDevice(device);
      if (!normalized.deviceId) {
        return;
      }
      byId.set(normalized.deviceId, normalized);
    });

    (systemDevices || []).forEach((device) => {
      const normalized = normalizeDevice(device);
      if (!normalized.deviceId) {
        return;
      }

      if (!byId.has(normalized.deviceId)) {
        byId.set(normalized.deviceId, normalized);
        return;
      }

      const existing = byId.get(normalized.deviceId);
      if (!existing.label && normalized.label) {
        byId.set(normalized.deviceId, { ...existing, label: normalized.label });
      }
    });

    return Array.from(byId.values());
  };

  const applyMicDevice = async (deviceId) => {
    const currentActiveDeviceId = activeMicDeviceIdRef.current;
    if (!deviceId || deviceId === currentActiveDeviceId) {
      return;
    }

    if (applyingRef.current) {
      pendingDeviceIdRef.current = deviceId;
      return;
    }

    applyingRef.current = true;

    try {
      if (typeof meetingManager?.startAudioInputDevice === 'function') {
        await meetingManager.startAudioInputDevice(deviceId);
      }
      activeMicDeviceIdRef.current = deviceId;
      setActiveMicDeviceId(deviceId);
      if (typeof onAudioInputSelected === 'function') {
        onAudioInputSelected(deviceId);
      }
    } catch (_error) {
      // Non-fatal.
    } finally {
      applyingRef.current = false;
      const pendingDeviceId = pendingDeviceIdRef.current;
      pendingDeviceIdRef.current = '';
      if (pendingDeviceId && pendingDeviceId !== activeMicDeviceIdRef.current) {
        applyMicDevice(pendingDeviceId);
      }
    }
  };

  const syncDevices = async ({
    sessionDevices,
    systemDevices,
    preferNewSystemDevice,
  }) => {
    const mergedDevices = mergeDevices(sessionDevices, systemDevices);
    setMenuDevices(mergedDevices);

    if (!mergedDevices.length) {
      knownIdsRef.current = new Set();
      return;
    }

    const currentIds = new Set(mergedDevices.map((device) => device.deviceId));
    const previousIds = knownIdsRef.current;
    const addedSystemDevices = (systemDevices || []).filter((device) => !previousIds.has(device.deviceId));
    knownIdsRef.current = currentIds;

    const currentActiveDeviceId = activeMicDeviceIdRef.current;
    const currentActiveExists = !!(currentActiveDeviceId && currentIds.has(currentActiveDeviceId));
    const fallbackId = mergedDevices[0]?.deviceId || '';
    const newSystemDeviceId = addedSystemDevices.length
      ? addedSystemDevices[addedSystemDevices.length - 1].deviceId
      : '';

    const nextDeviceId = preferNewSystemDevice && newSystemDeviceId
      ? newSystemDeviceId
      : (currentActiveExists ? currentActiveDeviceId : fallbackId);

    if (!nextDeviceId || nextDeviceId === currentActiveDeviceId) {
      return;
    }

    await applyMicDevice(nextDeviceId);
  };

  useEffect(() => {
    syncDevices({
      sessionDevices: audioInputDevices || [],
      systemDevices: [],
      preferNewSystemDevice: false,
    });
  }, [
    audioInputDevices,
  ]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const syncFromSystem = async () => {
      try {
        const canEnumerate = typeof navigator !== 'undefined' && navigator.mediaDevices?.enumerateDevices;
        if (!canEnumerate) {
          return;
        }

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const liveDevices = (allDevices || []).filter((device) => device.kind === 'audioinput');
        if (cancelled) {
          return;
        }

        await syncDevices({
          sessionDevices: audioInputDevicesRef.current || [],
          systemDevices: liveDevices,
          preferNewSystemDevice: true,
        });
      } catch (_error) {
        // Non-fatal.
      }
    };

    syncFromSystem();

    const handleDeviceChange = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        syncFromSystem();
      }, 180);
    };

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [
    meetingManager,
    onAudioInputSelected,
    setMenuDevices,
  ]);
};

export default useWaitingRoomAudioInputSync;
